import { type ColumnDef } from '@tanstack/react-table';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { formatTypeLabel } from '@/hooks/data/inspector/useUploadStore';
import { Badge } from '@/components/ui/badge';
import { ColumnType } from '@/types/column';
import { GalleryView } from '@/types';
import { cn } from '@/lib/utils';

interface TypeColumnOptions<T> {
  id?: string;
  accessorFn?: (row: T) => any;
  accessorKey: keyof T;
  label: string;
  galleryView: GalleryView;
  enableSorting?: boolean;
  enableHiding?: boolean;
  minSize?: number;
  maxSize?: number;
  size?: number;
  cellClassName?: string;
  headerClassName?: string;
  badgeClass?: string;
  dataCy?: string;
  /** Rows whose value is a mimetype should supply this, since some mimetypes cover several formats. */
  fileNameFn?: (row: T) => string | undefined | null;
}

export const TypeColumn = <T extends Record<string, any>>(opts: TypeColumnOptions<T>): ColumnDef<T> => {
  const {
    id = ColumnType.Type,
    accessorFn,
    accessorKey,
    label,
    galleryView,
    size = 80,
    cellClassName,
    headerClassName,
    enableSorting = false,
    enableHiding = true,
    minSize = 30,
    maxSize = 120,
    badgeClass,
    dataCy,
    fileNameFn,
  } = opts;

  return {
    id,
    enableSorting,
    ...(accessorFn && { accessorFn }),
    enableHiding,
    minSize,
    maxSize,
    accessorKey,
    header: () => label,
    meta: {
      cellClassName: cn('capitalize', cellClassName),
      ...(headerClassName && { headerClassName: headerClassName }),
      ...(badgeClass && { badgeClass }),
    },
    size,
    cell: ({ getValue, row }) => {
      const raw = getValue();

      if (raw === null || raw === undefined || raw === '') {
        return '';
      }

      const rawText = String(raw);
      const text = formatTypeLabel(rawText, fileNameFn?.(row.original));

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {galleryView === GalleryView.LIST ? (
              <p
                className="text-text-gray"
                data-cy={dataCy ?? `${galleryView ? galleryView : 'list'}-${row.index}-${id}`}
              >
                {text}
              </p>
            ) : (
              <Badge
                variant="outline"
                size="sm"
                className={cn('capitalize', badgeClass)}
                data-cy={dataCy ?? `${galleryView ? galleryView : 'list'}-${row.index}-${id}`}
              >
                {text}
              </Badge>
            )}
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="max-w-44 break-all capitalize">{rawText}</TooltipContent>
          </TooltipPortal>
        </Tooltip>
      );
    },
  };
};
